import { useCreateContextHook } from 'contexts/useCreateContextHook';
import React, {
    createContext,
    useCallback,
    useMemo,
    useState,
    useEffect,
    useRef,
} from 'react';
import { useSearchBarContext } from 'components/Filters/context/SearchBarContext/SearchBarContext';
import { neverop } from 'utils';
import { isRectOutside } from 'helpers/boundingClientRect/isRectOutside';
import { isRectsOverlapping } from 'helpers/boundingClientRect/isRectsOverlapping';
import { FilteredSuggestions } from '../types';

export type SuggestionsPillsContextType = {
    hasOverflow: boolean;
    suggestions: FilteredSuggestions[] | null;
    getIsOverlappingPill: (pillId: string) => boolean;
    registerPill: (
        pillId: string,
        ref: React.RefObject<HTMLDivElement>,
    ) => void;
};

export const defaultSuggestionsPillsContextContext: SuggestionsPillsContextType =
    {
        hasOverflow: false,
        suggestions: null,
        getIsOverlappingPill: neverop(
            'SuggestionsPillsContext',
            'getIsOverlappingPill',
            false,
        ),
        registerPill: neverop('SuggestionsPillsContext', 'registerPill'),
    };

export const SuggestionsPillsContext =
    createContext<SuggestionsPillsContextType>(
        defaultSuggestionsPillsContextContext,
    );

const MaxSuggestions = 60;

const useSuggestionsPillsProvider = ({
    containerRef,
    overflowRef,
    buttonRef,
}: {
    containerRef: React.RefObject<HTMLDivElement>;
    overflowRef: React.RefObject<HTMLDivElement>;
    buttonRef: React.RefObject<HTMLDivElement>;
}) => {
    const { suggestionsFromFilters } = useSearchBarContext();

    const filteredSuggestionsFromFilters = useMemo(
        () => suggestionsFromFilters.slice(0, MaxSuggestions),
        [suggestionsFromFilters],
    );

    const [hasOverflow, setHasOverflow] = useState(false);
    const [overlappingPills, setOverlappingPills] = useState<Set<string>>(
        new Set(),
    );
    const pillRefs = useRef<Map<string, React.RefObject<HTMLDivElement>>>(
        new Map(),
    );

    const registerPill = useCallback(
        (pillId: string, ref: React.RefObject<HTMLDivElement>) => {
            pillRefs.current.set(pillId, ref);
        },
        [],
    );

    // Check for pills outside the container
    const checkOverflowPills = useCallback(() => {
        const overflowElement = overflowRef.current;

        if (!overflowElement) {
            setHasOverflow(false);
            return;
        }

        const overflowRect = overflowElement.getBoundingClientRect();

        const hasAnyOverflow = Array.from(pillRefs.current.values()).some(
            (pillRef) => {
                const pillElement = pillRef.current;

                if (!pillElement) return false;

                const pillRect = pillElement.getBoundingClientRect();

                return isRectOutside(pillRect, overflowRect);
            },
        );

        setHasOverflow(hasAnyOverflow);
    }, [overflowRef]);

    const checkOverlappingPills = useCallback(() => {
        const viewMoreButton = buttonRef.current;

        if (!viewMoreButton) return;

        const viewMoreButtonRect = viewMoreButton.getBoundingClientRect();

        const overlappingPillIds = new Set<string>();

        pillRefs.current.forEach((pillRef, pillId) => {
            const pillElement = pillRef.current;

            if (!pillElement) return;

            const pillRect = pillElement.getBoundingClientRect();

            if (isRectsOverlapping(pillRect, viewMoreButtonRect)) {
                overlappingPillIds.add(pillId);
            }
        });

        setOverlappingPills(overlappingPillIds);
    }, [buttonRef]);

    // check overflow and overlapping pills when suggestions change
    useEffect(() => {
        checkOverflowPills();
        checkOverlappingPills();
    }, [
        filteredSuggestionsFromFilters,
        checkOverflowPills,
        checkOverlappingPills,
    ]);

    // check overflow and overlapping pills when container bounding rect changes
    useEffect(() => {
        const containerElement = containerRef.current;

        const resizeObserver = new ResizeObserver(() => {
            checkOverflowPills();
            checkOverlappingPills();
        });

        containerElement && resizeObserver.observe(containerElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [checkOverflowPills, checkOverlappingPills, containerRef]);

    const getIsOverlappingPill = useCallback(
        (pillId: string) => {
            return overlappingPills.has(pillId);
        },
        [overlappingPills],
    );

    return {
        hasOverflow,
        suggestions: filteredSuggestionsFromFilters,
        getIsOverlappingPill,
        registerPill,
    };
};

export const SuggestionsPillsProvider: React.FC<{
    containerRef: React.RefObject<HTMLDivElement>;
    overflowRef: React.RefObject<HTMLDivElement>;
    buttonRef: React.RefObject<HTMLDivElement>;
    children: React.ReactNode;
}> = ({ children, containerRef, overflowRef, buttonRef }) => {
    const { hasOverflow, suggestions, getIsOverlappingPill, registerPill } =
        useSuggestionsPillsProvider({ containerRef, overflowRef, buttonRef });

    return (
        <SuggestionsPillsContext.Provider
            value={{
                hasOverflow,
                suggestions,
                getIsOverlappingPill,
                registerPill,
            }}
        >
            {children}
        </SuggestionsPillsContext.Provider>
    );
};

export const useSuggestionsPillsContext = () => {
    const context = useCreateContextHook({
        context: SuggestionsPillsContext,
        errorMessage:
            'useSuggestionsPillsContext must be used within a SuggestionsPillsContext',
    });

    return context;
};

export default SuggestionsPillsProvider;
